import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Award, Info } from 'lucide-react';
import { datumUndZeit } from '../lib/zeit';

// Fahrer-Status (Fahrer-Paket A6/A7) — Admin-Panel für /admin/status-config.
// 3 Stufen (Namen frei, bewusst nicht Ubers Wörter), Punkte je Fahrt (3 im Peak, Quartal),
// GATES (Owner): Annahme ≥ X %, Storno ≤ Y %, Bewertung ≥ Z — verletzt = Status ruht, kein Bonus.
// Privilegien: Matching-Tie-Breaker, lange Fahrten zur Top-Stufe, Reservierungs-Early-Access.
// Master-Schalter Default AUS — Matching bleibt ohne Aktivierung byte-gleich.

const inp = { width: '100%', padding: '7px 10px', background: '#e5e7eb', border: '1.5px solid #d1d5db', borderRadius: 9, color: '#0a0a0a', fontSize: 12, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };
const lbl = { fontSize: 12, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 };
const btn = (color = '#9c27b0') => ({ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: color, color: '#fff', borderRadius: 9, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' });
const box = { background: '#fff', borderRadius: 10, padding: 14, border: '1px solid #e5e7eb' };
const boxTitle = { fontSize: 13, fontWeight: 800, marginBottom: 10 };
const th = { padding: '6px 8px', fontWeight: 700, whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', whiteSpace: 'nowrap' };

export default function StatusPanel() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [pairs, setPairs] = useState(null);   // Ebene ④: Prüfliste, rein lesend
  const [pairsError, setPairsError] = useState(false);
  const pairsRequest = useRef(0);

  const load = () => api.get('/admin/status-config').then(r => {
    setCfg(r.data);
    setDraft({
      names: [...r.data.names],
      tier2: String(r.data.tier2_points), tier3: String(r.data.tier3_points),
      gAccept: String(r.data.gate_acceptance_pct), gCancel: String(r.data.gate_cancel_pct), gRating: String(r.data.gate_rating),
      mWeight: String(r.data.match_weight_min), longKm: String(r.data.long_ride_km), longW: String(r.data.long_ride_weight_min),
      dMid: String(r.data.resv_delay_mid_min), dLow: String(r.data.resv_delay_low_min),
      fdRest: String(r.data.fd_max_rest_min), fdMalus: String(r.data.fd_malus_min),
      qKm: String(r.data.incentive_qualification?.minDrivenKm ?? 1.5),
      qMin: String(r.data.incentive_qualification?.minDurationMin ?? 3),
      qRatio: String(r.data.incentive_qualification?.minDurationRatio ?? 0.4),
      qAccuracy: String(r.data.incentive_qualification?.maxAccuracyM ?? 100),
      pRides: String(r.data.pair_limit?.limit ?? 5),
      pDays: String(r.data.pair_limit?.windowDays ?? 30),
      cNear: String(r.data.collusion?.nearMeters ?? 50),
      cFast: String(r.data.collusion?.fastAcceptMs ?? 3000),
      cDays: String(r.data.collusion?.windowDays ?? 30),
      cMin: String(r.data.collusion?.minRides ?? 2),
      peaks: JSON.stringify(r.data.peak_windows),
    });
  }).catch(() => {});

  // Ebene ④: Die Prüfliste liest nur. Wiederholen startet ausschließlich denselben GET erneut.
  const loadPairs = () => {
    const request = ++pairsRequest.current;
    setPairsError(false);
    return api.get('/admin/collusion/pairs').then(r => {
      if (request === pairsRequest.current) setPairs(r.data);
    }).catch(() => {
      if (request === pairsRequest.current) {
        setPairs(null);
        setPairsError(true);
      }
    });
  };
  useEffect(() => { load(); loadPairs(); }, []);

  const save = async (updates, { refreshPairs = false } = {}) => {
    try {
      await api.post('/admin/status-config', updates);
      await load();
      if (refreshPairs) await loadPairs();
      toast.success('Übernommen');
    }
    catch { toast.error('Fehler'); }
  };

  const saveAll = () => {
    let peaks;
    try { peaks = JSON.parse(draft.peaks); } catch { return toast.error('Peak-Fenster: ungültiges JSON'); }
    save({
      names: draft.names,
      tier2_points: parseFloat(draft.tier2), tier3_points: parseFloat(draft.tier3),
      gate_acceptance_pct: parseFloat(draft.gAccept), gate_cancel_pct: parseFloat(draft.gCancel), gate_rating: parseFloat(draft.gRating),
      match_weight_min: parseFloat(draft.mWeight), long_ride_km: parseFloat(draft.longKm), long_ride_weight_min: parseFloat(draft.longW),
      resv_delay_mid_min: parseFloat(draft.dMid), resv_delay_low_min: parseFloat(draft.dLow),
      fd_max_rest_min: parseFloat(draft.fdRest), fd_malus_min: parseFloat(draft.fdMalus),
      bonus_min_driven_km: parseFloat(draft.qKm), bonus_min_duration_min: parseFloat(draft.qMin),
      bonus_min_duration_ratio: parseFloat(draft.qRatio),
      bonus_trace_max_accuracy_m: parseFloat(draft.qAccuracy),
      pair_limit_rides: parseFloat(draft.pRides), pair_limit_window_days: parseFloat(draft.pDays),
      collusion_near_meters: parseFloat(draft.cNear), collusion_fast_accept_ms: parseFloat(draft.cFast),
      collusion_report_window_days: parseFloat(draft.cDays), collusion_report_min_rides: parseFloat(draft.cMin),
      peak_windows: peaks,
    }, { refreshPairs: true });
  };

  const onOff = (active, onClick) => (
    <button onClick={onClick} style={{ border: 'none', cursor: 'pointer', borderRadius: 999, padding: '4px 14px', fontSize: 12, fontWeight: 800, color: '#fff', background: active ? '#16a34a' : '#6b7280' }}>
      {active ? 'AN' : 'AUS'}
    </button>
  );

  if (!cfg || !draft) return <p style={{ fontSize: 12, color: '#6b7280' }}>lädt…</p>;

  return (
    <div style={{ background: '#ffffff', borderRadius: 16, border: '1px solid #e5e7eb', padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: '#9c27b022', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Award size={20} color="#9c27b0" />
        </div>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a', margin: 0 }}>Fahrer-Status</h2>
          <p style={{ fontSize: 12, color: '#4b5563', margin: 0 }}>3 Stufen · Punkte je Fahrt (3 im Peak) · Privilegien statt Geld</p>
        </div>
        <div style={{ marginLeft: 'auto' }}>{onOff(cfg.active, () => save({ active: !cfg.active }))}</div>
      </div>
      <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 16px 0', display: 'flex', gap: 6, alignItems: 'center' }}>
        <Info size={13} /> Solange AUS: Matching und Reservierungen bleiben exakt wie heute. Gates verletzt = Status ruht, keine Privilegien.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={box}>
          <div style={boxTitle}>Stufen &amp; Punkte (Quartal)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
            {[0, 1, 2].map(i => (
              <div key={i}><label style={lbl}>Name Stufe {i + 1}</label>
                <input value={draft.names[i]} onChange={e => setDraft(p => { const n = [...p.names]; n[i] = e.target.value; return { ...p, names: n }; })} style={inp} /></div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div><label style={lbl}>Ab Punkte: Stufe 2</label><input type="number" value={draft.tier2} onChange={e => setDraft(p => ({ ...p, tier2: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Ab Punkte: Stufe 3</label><input type="number" value={draft.tier3} onChange={e => setDraft(p => ({ ...p, tier3: e.target.value }))} style={inp} /></div>
          </div>
        </div>

        <div style={box}>
          <div style={boxTitle}>Gates (sonst ruht der Status)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div><label style={lbl}>Annahme ≥ %</label><input type="number" value={draft.gAccept} onChange={e => setDraft(p => ({ ...p, gAccept: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Storno ≤ %</label><input type="number" value={draft.gCancel} onChange={e => setDraft(p => ({ ...p, gCancel: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Bewertung ≥</label><input type="number" step="0.05" value={draft.gRating} onChange={e => setDraft(p => ({ ...p, gRating: e.target.value }))} style={inp} /></div>
          </div>
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8, marginBottom: 0 }}>Gemessen über die laufende Woche (Mo–Mo), gleiche Zahlen wie Fahrer-/Partner-Ansicht.</p>
        </div>

        <div style={box}>
          <div style={boxTitle}>Privilegien (Matching &amp; Reservierungen)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div><label style={lbl}>Tie-Breaker (Min/Stufe)</label><input type="number" step="0.1" value={draft.mWeight} onChange={e => setDraft(p => ({ ...p, mWeight: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Lange Fahrt ab km</label><input type="number" value={draft.longKm} onChange={e => setDraft(p => ({ ...p, longKm: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Gewicht lang (Min/Stufe)</label><input type="number" step="0.1" value={draft.longW} onChange={e => setDraft(p => ({ ...p, longW: e.target.value }))} style={inp} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div><label style={lbl}>Reservierung: Stufe 2 nach Min</label><input type="number" value={draft.dMid} onChange={e => setDraft(p => ({ ...p, dMid: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Stufe 1 nach Min</label><input type="number" value={draft.dLow} onChange={e => setDraft(p => ({ ...p, dLow: e.target.value }))} style={inp} /></div>
          </div>
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8, marginBottom: 0 }}>Top-Stufe sieht Flotten-Freigaben sofort. Das Partner-Vetorecht (Re-Zuweisung) bleibt immer.</p>
        </div>

        <div style={box}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ ...boxTitle, marginBottom: 0 }}>Forward Dispatch (Folgefahrt)</div>
            {onOff(cfg.fd_shape_active, () => save({ fd_shape_active: !cfg.fd_shape_active }))}
          </div>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px 0' }}>
            AN: Folgefahrt-Angebot nur, wenn der Fahrer „gleich frei" ist; freie Fahrer gewinnen bei Gleichstand.
            AUS = heutiges Verhalten. Der Aus-Schalter des Fahrers gilt immer.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div><label style={lbl}>„Gleich frei" ≤ Min zum Ziel</label><input type="number" value={draft.fdRest} onChange={e => setDraft(p => ({ ...p, fdRest: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Malus (Min)</label><input type="number" value={draft.fdMalus} onChange={e => setDraft(p => ({ ...p, fdMalus: e.target.value }))} style={inp} /></div>
          </div>
        </div>

        <div style={{ ...box, gridColumn: '1 / -1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ ...boxTitle, marginBottom: 0 }}>Anrechnung von Fahrten (Bonus · Punkte · Annahmequote)</div>
            {onOff(cfg.incentive_qualification?.active, () => save({ incentive_qualification_active: !cfg.incentive_qualification?.active }))}
          </div>
          <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 8px 0' }}>
            Eine abgeschlossene Fahrt zählt nur dann für Partner-Bonus, Status-Punkte und Annahmequote,
            wenn sie diese Schwellen erfüllt. <b>Die Fahrt selbst wird nie verhindert</b> — sie ist normal
            abgerechnet und bezahlt, der Fahrgast merkt nichts. So wird das gegenseitige Bestellen unter
            Fahrern wertlos, ohne dass einer ehrlichen Fahrt etwas passiert. AUS = jede Fahrt zählt (wie früher).
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
            <div><label style={lbl}>Mindest-Strecke (km)</label><input type="number" step="0.1" value={draft.qKm} onChange={e => setDraft(p => ({ ...p, qKm: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Mindest-Dauer (Min)</label><input type="number" step="0.5" value={draft.qMin} onChange={e => setDraft(p => ({ ...p, qMin: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Anteil der geplanten Zeit</label><input type="number" step="0.05" value={draft.qRatio} onChange={e => setDraft(p => ({ ...p, qRatio: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>max. GPS-Ungenauigkeit (m)</label><input type="number" value={draft.qAccuracy} onChange={e => setDraft(p => ({ ...p, qAccuracy: e.target.value }))} style={inp} /></div>
          </div>
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 6, marginBottom: 0 }}>
            Gemessen wird die <b>tatsächlich gefahrene</b> Strecke aus der Positionsspur; ohne brauchbare Spur
            die geplante Route. Der Zeit-Anteil schließt die Lücke „lang buchen, nicht fahren": wer in unter
            {' '}{Math.round((Number(draft.qRatio) || 0) * 100)} % der vorhergesagten Fahrzeit fertig ist, zählt nicht.
            Änderungen gelten nur für <b>künftige</b> Abschlüsse — nichts wird rückwirkend aberkannt.
          </p>
        </div>

        <div style={{ ...box, gridColumn: '1 / -1' }}>
          <div style={boxTitle}>Prüfliste: Merkmale und Zeitraum</div>
          <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 8px 0' }}>
            Diese Werte steuern ausschließlich die lesende Auswertung unten. Sie sperren keine Fahrt
            und ändern weder Bonus noch Anrechnung.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
            <div><label style={lbl}>Nähe bis Meter</label><input type="number" value={draft.cNear} onChange={e => setDraft(p => ({ ...p, cNear: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>schnell bis Millisekunden</label><input type="number" value={draft.cFast} onChange={e => setDraft(p => ({ ...p, cFast: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Zeitraum (Tage)</label><input type="number" value={draft.cDays} onChange={e => setDraft(p => ({ ...p, cDays: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Paar ab Fahrten</label><input type="number" value={draft.cMin} onChange={e => setDraft(p => ({ ...p, cMin: e.target.value }))} style={inp} /></div>
          </div>
        </div>

        <div style={{ ...box, gridColumn: '1 / -1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ ...boxTitle, marginBottom: 0 }}>Paar-Grenze (derselbe Kunde beim selben Fahrer)</div>
            {onOff(cfg.pair_limit?.active, () => save({ pair_limit_active: !cfg.pair_limit?.active }))}
          </div>
          <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 8px 0' }}>
            Fährt ein Kunde im Zeitfenster öfter als erlaubt mit demselben Fahrer, greift eine Folge —
            welche, entscheidet die <b>Betriebsart</b>. Wer die Paarung nicht frei wiederholen kann,
            kann sich nicht absprechen.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
            <div>
              <label style={lbl}>Betriebsart</label>
              <select value={cfg.pair_limit?.mode || 'not_counted'}
                onChange={e => save({ pair_limit_mode: e.target.value })} style={inp}>
                <option value="not_counted">zählt nicht — Fahrt findet statt, bringt aber nichts</option>
                <option value="blocked">geht nicht — Fahrer bekommt kein Angebot mehr</option>
              </select>
            </div>
            <div><label style={lbl}>Gemeinsame Fahrten</label><input type="number" value={draft.pRides} onChange={e => setDraft(p => ({ ...p, pRides: e.target.value }))} style={inp} /></div>
            <div><label style={lbl}>Zeitfenster (Tage)</label><input type="number" value={draft.pDays} onChange={e => setDraft(p => ({ ...p, pDays: e.target.value }))} style={inp} /></div>
          </div>
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 6, marginBottom: 0 }}>
            <b>„zählt nicht"</b> (Startwert): das Matching bleibt unberührt — <b>kein Fahrgast bleibt je
            stehen</b>. Die Folge trifft aber den <b>Fahrer</b>: er fährt und bekommt dafür keinen Bonus,
            keinen Punkt, keine Annahme. Bei zwei abgesprochenen Kollegen ist das gewollt; bei einem
            ehrlichen Stammkunden träfe es einen Unschuldigen. <b>Deshalb hier eher weit einstellen</b> —
            der Startwert 5 ist die bewusste Mitte (bei 3 verlöre ein Fahrer mit Stammkunden rund jede
            fünfte Fahrt aus der Bonusrechnung, ab 8 wäre es überall ruhig).<br />
            <b>„geht nicht"</b>: schärfer gegen Absprachen, aber ein Vielfahrer-Kunde in kleiner Flotte
            kann ohne Auto dastehen (Obergrenze = Grenze × Fahrerzahl).<br />
            Jeder Fall steht unten in der Prüfliste — dort nachsehen, ob es ehrliche Stammkunden trifft.
          </p>
        </div>

        <div style={{ ...box, gridColumn: '1 / -1' }}>
          <div style={boxTitle}>Peak-Fenster (3 Punkte je Fahrt · zählt auch für die Peak-Stunden der Leistung)</div>
          <textarea value={draft.peaks} onChange={e => setDraft(p => ({ ...p, peaks: e.target.value }))} rows={2}
            style={{ ...inp, fontFamily: 'monospace', fontSize: 12 }} />
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 6, marginBottom: 0 }}>
            JSON: days 1=Mo … 7=So, from/to = volle Stunden (Berlin). Beispiel: {'[{"days":[1,2,3,4,5],"from":16,"to":19}]'}
          </p>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <button onClick={saveAll} style={btn()}>Speichern</button>
      </div>

      {/* Ebene ④ — Prüfliste „auffällige Paare". REIN LESEND: kein Knopf, keine Sperre, kein
          Bonus-Entzug. Bewusst ohne Gesamtnote und ohne Ampel — eine Zahl würde suggerieren,
          die Maschine habe schon geurteilt. Entscheiden soll ein Mensch mit den Belegen. */}
      <div style={{ ...box, marginTop: 20 }}>
        <div style={boxTitle}>Auffällige Paare (Kunde ↔ Fahrer)</div>
        <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 10px 0' }}>
          Nur eine <b>Beobachtung</b> — hier wird nichts gesperrt und kein Bonus entzogen.
          Ein Fahrer darf privat Fahrgast sein, und wenige Meter Abstand sind am Taxistand oder
          vor dem Hotel völlig normal. <b>Erst die Kombination</b> mehrerer Spalten ergibt ein Bild,
          und entscheiden muss ein Mensch.
          {pairs ? ` Zeitraum: ${pairs.window_days} Tage · „nah" = ${pairs.near_meters} m.` : ''}
        </p>
        {!pairs && !pairsError && <p style={{ fontSize: 12, color: '#6b7280' }}>wird geladen …</p>}
        {pairsError && (
          <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#b91c1c', fontSize: 12 }}>
            <span>Prüfliste konnte nicht geladen werden.</span>
            <button type="button" onClick={loadPairs} style={btn('#b91c1c')}>Erneut laden</button>
          </div>
        )}
        {pairs && pairs.pairs.length === 0 && (
          <p style={{ fontSize: 12, color: '#16a34a', margin: 0 }}>Nichts Auffälliges im Zeitraum.</p>
        )}
        {pairs && pairs.pairs.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#4b5563' }}>
                  <th style={th}>Kunde</th><th style={th}>Fahrer</th>
                  <th style={th}>Fahrten</th><th style={th}>nicht angerechnet</th>
                  <th style={th}>Kunde ist Fahrer</th><th style={th}>aus der Nähe</th>
                  <th style={th}>sofort angenommen</th><th style={th}>bar</th>
                  <th style={th}>Ø gebucht</th><th style={th}>Ø gefahren</th>
                  <th style={th}>Paar-Sperren</th><th style={th}>zuletzt</th>
                </tr>
              </thead>
              <tbody>
                {pairs.pairs.map((p) => (
                  <tr key={p.customer_id + p.driver_id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={td}>{p.customer_name || p.customer_id}</td>
                    <td style={td}>{p.driver_name || p.driver_id}</td>
                    <td style={td}>{p.rides_together}</td>
                    <td style={td}>{p.not_counted}</td>
                    <td style={td}>{p.customer_is_driver_rides}</td>
                    <td style={td}>{p.near_accepts}{p.min_accept_distance_m != null ? ` (min ${Math.round(p.min_accept_distance_m)} m)` : ''}</td>
                    <td style={td}>{p.fast_accepts}</td>
                    <td style={td}>{p.cash_rides}</td>
                    <td style={td}>{p.avg_booked_km != null ? `${p.avg_booked_km} km` : '—'}</td>
                    <td style={td}>{p.avg_driven_km != null ? `${p.avg_driven_km} km` : '—'}</td>
                    <td style={td}>{p.pair_blocks}</td>
                    <td style={td}>{datumUndZeit(p.last_ride_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
